import type { PublicConfig } from "../lib/api";

export function confirmedSupportEmail(config: PublicConfig | null) {
  const email = config?.supportEmail;
  return config?.supportEmailConfirmed === true && typeof email === "string" && /^[A-Za-z0-9.!#$%&'*+/=?^_`{|}~-]+@[A-Za-z0-9-]+(?:\.[A-Za-z0-9-]+)+$/.test(email) ? email : null;
}

/**
 * Returns a public Instagram profile only after the backend has explicitly
 * confirmed it.  A single profile path is deliberately required so that an
 * arbitrary Instagram page (or an impersonating subdomain) cannot become a
 * public contact link.
 */
export function confirmedInstagramUrl(config: PublicConfig | null) {
  const value = config?.instagramUrl;
  if (config?.instagramConfirmed !== true || typeof value !== "string" || value.length > 160 || value !== value.trim()) return null;
  try {
    const url = new URL(value);
    const host = url.hostname.toLowerCase();
    if (url.protocol !== "https:" || !["instagram.com", "www.instagram.com"].includes(host) || url.username || url.password || url.port || url.search || url.hash) return null;
    const match = /^\/([A-Za-z0-9._]{1,30})\/?$/.exec(url.pathname);
    if (!match || ["about", "accounts", "developer", "direct", "explore", "p", "reel", "reels", "stories", "web"].includes(match[1].toLowerCase())) return null;
    return `https://www.instagram.com/${match[1]}/`;
  } catch {
    return null;
  }
}

/**
 * Builds WhatsApp's canonical HTTPS click-to-chat link from a confirmed E.164
 * number.  The config never supplies a link directly, avoiding arbitrary URL
 * schemes, query strings, or a potentially misleading destination.
 */
export function confirmedWhatsAppLink(config: PublicConfig | null) {
  const value = config?.whatsappNumber;
  if (config?.whatsappConfirmed !== true || typeof value !== "string" || !/^\+[1-9]\d{7,14}$/.test(value)) return null;
  return `https://wa.me/${value.slice(1)}`;
}

// Keep both common spellings available for callers while using one validator.
export const confirmedWhatsAppUrl = confirmedWhatsAppLink;
export const confirmedWhatsappUrl = confirmedWhatsAppLink;

export function googleAuthAvailable(config: PublicConfig | null) {
  const capabilities = config?.capabilities;
  return config?.configured === true && capabilities !== null && typeof capabilities === "object" && (capabilities as Record<string, unknown>).googleAuth === true;
}
export function googleAuthorizationLink(value: unknown) {
  if (typeof value !== "string") return null;
  try { const url = new URL(value); return url.protocol === "https:" && url.hostname === "accounts.google.com" && !url.username && !url.password && (!url.port || url.port === "443") && ["/o/oauth2/v2/auth", "/o/oauth2/auth"].includes(url.pathname) ? url.toString() : null; } catch { return null; }
}
export function googleResultMessage(result: string) {
  return ({ cancelled: "Accesso con Google annullato.", invalid_state: "Il collegamento di accesso è scaduto. Riprova.", account_link_required: "Questa e-mail ha già un account. Accedi con la tua password.", registration_required: "Per iniziare con Google, scegli Crea account e accetta i termini.", registration_closed: "Le nuove iscrizioni si apriranno presto.", provider_error: "Non è stato possibile completare l’accesso con Google. Riprova.", not_configured: "L’accesso con Google sarà disponibile presto." } as Record<string, string>)[result] || "L’accesso non è stato completato. Riprova.";
}
